type CaptureContext = Record<string, unknown>;

type SentryClient = {
    captureException: (error: unknown, context?: CaptureContext) => void;
};

type SentryModule = typeof import('@sentry/react');

const queue: Array<{ error: unknown; context?: CaptureContext }> = [];
const PENDING_ERRORS_KEY = 'sentry.pending-errors';
let client: SentryClient | null = null;
let sentryModule: SentryModule | null = null;
let listening = false;
let initialization: Promise<void> | null = null;
let desiredAuthenticated = false;
let replayIntegrationInstalled = false;
let replayWasStopped = false;

function restorePendingErrors(): void {
    try {
        const pending = JSON.parse(sessionStorage.getItem(PENDING_ERRORS_KEY) ?? '[]') as unknown;

        if (Array.isArray(pending)) {
            for (const item of pending) {
                if (typeof item === 'object' && item !== null && 'message' in item) {
                    const serialized = item as { message: unknown; name?: unknown; stack?: unknown };
                    const error = new Error(String(serialized.message));
                    error.name = typeof serialized.name === 'string' ? serialized.name : 'Error';
                    error.stack = typeof serialized.stack === 'string' ? serialized.stack : undefined;
                    queue.push({ error });
                }
            }
        }

        sessionStorage.removeItem(PENDING_ERRORS_KEY);
    } catch {
        try {
            sessionStorage.removeItem(PENDING_ERRORS_KEY);
        } catch {
            // Storage may be unavailable; the current document still has its queue.
        }
    }
}

function persistPendingErrors(): void {
    if (client || queue.length === 0) {
        return;
    }

    const serialized = queue.map(({ error }) => {
        if (error instanceof Error) {
            return { name: error.name, message: error.message, stack: error.stack };
        }

        return { message: String(error) };
    });

    try {
        sessionStorage.setItem(PENDING_ERRORS_KEY, JSON.stringify(serialized));
    } catch {
        // Storage may be unavailable; keep the in-memory queue intact.
    }
}

export function captureException(error: unknown, context?: CaptureContext): void {
    if (client) {
        client.captureException(error, context);
        return;
    }

    queue.push({ error, context });
}

export function bindSentry(sentry: SentryClient): void {
    client = sentry;

    for (const item of queue) {
        sentry.captureException(item.error, item.context);
    }

    queue.length = 0;
}

export function listenForEarlyErrors(): void {
    if (listening || typeof window === 'undefined') {
        return;
    }

    listening = true;
    restorePendingErrors();
    window.addEventListener('pagehide', persistPendingErrors, { capture: true });

    window.addEventListener('error', (event) => {
        if (client) {
            return;
        }

        captureException(event.error ?? event.message);
        void initSentry().catch(() => undefined);
    });

    window.addEventListener('unhandledrejection', (event) => {
        if (client) {
            return;
        }

        captureException(event.reason);
        void initSentry().catch(() => undefined);
    });
}

async function syncReplayWithAuthentication(): Promise<void> {
    if (!sentryModule) {
        return;
    }

    let replay = sentryModule.getReplay();

    if (desiredAuthenticated) {
        if (!replayIntegrationInstalled) {
            sentryModule.addIntegration(sentryModule.replayIntegration());
            replayIntegrationInstalled = true;
            replay = sentryModule.getReplay();
        } else if (replay && replayWasStopped) {
            // Re-enable recording after an authenticated user logs back in
            // without a full document navigation.
            if (Math.random() < 0.5) {
                replay.start();
            } else {
                replay.startBuffering();
            }
        }

        replayWasStopped = false;
        return;
    }

    if (replay && !replayWasStopped) {
        await replay.stop();
        replayWasStopped = true;
    }
}

export function setSentryAuthenticated(authenticated: boolean): void {
    desiredAuthenticated = authenticated;
    void syncReplayWithAuthentication();
}

async function initializeSentry(): Promise<void> {
    const authenticatedAtInit = desiredAuthenticated;
    const Sentry = await import('@sentry/react');
    const integrations = [
        Sentry.browserTracingIntegration(),
        ...(import.meta.env.VITE_APP_ENV !== 'local' ? [Sentry.browserSessionIntegration()] : []),
        ...(import.meta.env.PROD
            ? [
                  Sentry.thirdPartyErrorFilterIntegration({
                      filterKeys: ['heykaution-frontend'],
                      behaviour: 'drop-error-if-exclusively-contains-third-party-frames',
                  }),
              ]
            : []),
    ];

    if (authenticatedAtInit) {
        integrations.push(Sentry.replayIntegration());
    }

    Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_DSN,
        integrations,
        tracesSampleRate: 0.1,
        // Replay is omitted entirely for anonymous pages, but these rates
        // remain available if an Inertia login adds the integration later.
        replaysSessionSampleRate: 0.5,
        replaysOnErrorSampleRate: 1.0,
        environment: import.meta.env.VITE_APP_ENV,
        release: import.meta.env.VITE_APP_VERSION || 'local',
        sendDefaultPii: import.meta.env.VITE_APP_ENV !== 'production',
    });

    sentryModule = Sentry;
    replayIntegrationInstalled = authenticatedAtInit;
    bindSentry(Sentry);
    await syncReplayWithAuthentication();

    if (import.meta.env.VITE_APP_ENV === 'local') {
        (window as typeof window & { Sentry: typeof Sentry }).Sentry = Sentry;
    }
}

export function initSentry(): Promise<void> {
    if (!initialization) {
        initialization = initializeSentry().catch((error: unknown) => {
            initialization = null;
            throw error;
        });
    }

    return initialization;
}

export function resetSentryBridge(): void {
    client = null;
    sentryModule = null;
    queue.length = 0;
    listening = false;
    initialization = null;
    desiredAuthenticated = false;
    replayIntegrationInstalled = false;
    replayWasStopped = false;
}
